import { useEffect, useState } from 'react';
import { Check, MapPin, Plus, Warehouse } from 'lucide-react';
import type { StoreroomDto } from '@eombor/contracts';
import { Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { useMyStorerooms } from '../model/subdivision.queries.js';
import { StoreroomDialog } from './StoreroomDialog.js';

export interface StoreroomPlace {
  storeroomId: string;
  zoneId: string | null;
}

export interface StoreroomPickerProps {
  value: StoreroomPlace | null;
  onChange: (next: StoreroomPlace) => void;
  /** Sarlavha — qabulda «Qayerga qo'yasiz?», ko'chirishda boshqasi. */
  title?: string;
  hint?: string;
  /** Tanlab bo'lmaydigan joy (ko'chirishda — lotning hozirgi joyi). */
  current?: StoreroomPlace | null;
}

/**
 * JOY TANLAGICH — ombor plitkalari va tanlangan omborning zonalari.
 *
 * Ro'yxat emas, PLITKA: omborlar odatda ikki-uchta, va ular nomi bilan
 * emas, «nima uchun» bilan eslanadi — shuning uchun maqsadi ham plitkada
 * ko'rinib turadi. Yangi omborni shu yerning o'zida ochish mumkin: xodim
 * qabul paytida «garajga qo'yaman» deb qaror qilsa, oynani yopib boshqa
 * sahifaga borishi kerak bo'lmasin.
 *
 * Bo'linmasi yo'q xodimda (so'rov 404) tanlagich umuman chizilmaydi.
 */
export const StoreroomPicker = ({ value, onChange, title, hint, current = null }: StoreroomPickerProps) => {
  const rooms = useMyStorerooms();
  const { canAnywhere } = usePermission();
  const [creating, setCreating] = useState(false);
  const active = (rooms.data ?? []).filter((room) => room.isActive);

  /* Birinchi yuklanishda birinchi ombor O'ZI tanlanadi — server ham shunday
     qiladi, ekran esa buni yashirmasdan ko'rsatadi. */
  useEffect(() => {
    if (value !== null) return;
    const first = active.find((room) => room.id !== current?.storeroomId) ?? active[0];
    if (first !== undefined) onChange({ storeroomId: first.id, zoneId: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- faqat ro'yxat kelganda.
  }, [rooms.data]);

  if (rooms.isPending) return <Skeleton className="h-28 w-full" />;
  if (rooms.isError) return null;

  const selected = active.find((room) => room.id === value?.storeroomId);
  const canCreate = canAnywhere('storeroom.manage');

  return (
    <section className="flex flex-col gap-3" aria-label={title ?? t('storeroom.pickTitle')}>
      <header className="flex flex-col gap-0.5">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-text">
          <MapPin size={15} aria-hidden className="text-accent" />
          {title ?? t('storeroom.pickTitle')}
        </h3>
        <p className="text-xs text-text-muted">
          {active.length === 0 ? t('storeroom.pickEmpty') : (hint ?? t('storeroom.pickHint'))}
        </p>
      </header>

      <div role="radiogroup" aria-label={t('storeroom.stripLabel')} className="grid gap-2 sm:grid-cols-2">
        {active.map((room) => (
          <RoomTile
            key={room.id}
            room={room}
            checked={room.id === value?.storeroomId}
            onSelect={() => onChange({ storeroomId: room.id, zoneId: null })}
          />
        ))}
        {canCreate ? (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className={cn(
              'flex min-h-[4.25rem] items-center justify-center gap-2 rounded-[var(--radius-md)]',
              'border border-dashed border-border-strong px-3 text-sm font-medium text-text-muted',
              'transition-colors duration-[var(--motion-fast)] hover:border-accent hover:text-accent',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
            )}
          >
            <Plus size={16} aria-hidden />
            {t('storeroom.add')}
          </button>
        ) : null}
      </div>

      {selected === undefined || selected.zones.length === 0 ? null : (
        <div role="radiogroup" aria-label={t('storeroom.zonesTitle')} className="flex flex-wrap gap-2">
          {[{ id: null, name: t('storeroom.noZone') }, ...selected.zones].map((zone) => {
            const checked = (value?.zoneId ?? null) === zone.id;
            const isCurrent = current?.storeroomId === selected.id && (current.zoneId ?? null) === zone.id;
            return (
              <button
                key={zone.id ?? 'none'}
                type="button"
                role="radio"
                aria-checked={checked}
                disabled={isCurrent}
                onClick={() => onChange({ storeroomId: selected.id, zoneId: zone.id })}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm',
                  'transition-colors duration-[var(--motion-fast)] disabled:cursor-not-allowed disabled:opacity-40',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                  checked
                    ? 'border-accent bg-accent text-[var(--color-accent-contrast)]'
                    : 'border-border bg-surface text-text hover:border-border-strong',
                )}
              >
                {checked ? <Check size={13} aria-hidden /> : null}
                {zone.name}
              </button>
            );
          })}
        </div>
      )}

      {creating ? (
        <StoreroomDialog
          open
          storeroom={null}
          onClose={() => setCreating(false)}
          onCreated={(created) => onChange({ storeroomId: created.id, zoneId: created.zones[0]?.id ?? null })}
        />
      ) : null}
    </section>
  );
};

const RoomTile = ({ room, checked, onSelect }: { room: StoreroomDto; checked: boolean; onSelect: () => void }) => (
  <button
    type="button"
    role="radio"
    aria-checked={checked}
    onClick={onSelect}
    className={cn(
      'group flex min-h-[4.25rem] items-start gap-3 rounded-[var(--radius-md)] border p-3 text-left',
      'transition-[border-color,background-color,box-shadow] duration-[var(--motion-fast)]',
      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
      checked
        ? 'border-accent bg-accent-soft shadow-[0_0_0_1px_var(--color-accent)]'
        : 'border-border bg-surface hover:border-border-strong',
    )}
  >
    <span
      aria-hidden
      className={cn(
        'flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-sm)]',
        checked ? 'bg-accent text-[var(--color-accent-contrast)]' : 'bg-surface-2 text-text-muted',
      )}
    >
      {checked ? <Check size={17} /> : <Warehouse size={17} />}
    </span>
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="truncate text-sm font-semibold text-text">{room.name}</span>
      <span className="truncate text-xs text-text-muted">
        {room.purpose ?? t('storeroom.lots', { count: room.lotCount })}
      </span>
    </span>
  </button>
);
